import { useEffect, useState } from "react";
import { toast } from "sonner";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Database, Download, Trash2, AlertTriangle, Shield, Wrench, Gift, Sparkles, FileSearch, ArrowLeft, History, Mail, RefreshCw, TrendingUp, Eye, MessageSquareWarning, Check, Archive, MessageCircleReply, Send, X, Plus } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";
import { Section, KpiTile, SCOPE_LABELS } from "./_shared";
import { useConfirm } from "@/components/ConfirmDialog";

// =========================== BACKUPS ===========================
function BackupsTab() {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [creating, setCreating] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreKey, setRestoreKey] = useState("");
  const [restoreConfirm, setRestoreConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Preview dialog state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (b) => {
    setPreviewOpen(true);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const { data } = await teacherApi.get(`/admin/backups/${b.id}/preview?sample_size=3`);
      setPreviewData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd podglądu backupu");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  };

  const load = async () => {
    const { data } = await teacherApi.get("/admin/backups");
    setList(data.backups || []);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true);
    try {
      await teacherApi.post("/admin/backup");
      toast.success("Backup utworzony");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd backupu");
    } finally { setCreating(false); }
  };

  const askRestore = (key) => {
    setRestoreKey(key);
    setRestoreConfirm("");
    setRestoreOpen(true);
  };
  const doRestore = async () => {
    if (restoreConfirm !== "RESTORE") {
      toast.error("Wpisz dokładnie RESTORE");
      return;
    }
    setBusy(true);
    try {
      const { data } = await teacherApi.post("/admin/restore", {
        backup_key: restoreKey, confirm: restoreConfirm,
      });
      toast.success(`Przywrócono z ${data.restored_from}`);
      setRestoreOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Błąd restore");
    } finally { setBusy(false); }
  };
  const remove = async (b) => {
    const ok = await confirm({
      title: "Usunąć backup?",
      description: `Backup „${b.key}" zostanie trwale usunięty z Object Storage. Operacja jest nieodwracalna.`,
      confirmText: "Usuń backup",
      variant: "danger",
    });
    if (!ok) return;
    await teacherApi.delete(`/admin/backups/${b.id}`);
    toast.success("Backup usunięty");
    load();
  };

  return (
    <Section icon={Database} title="Backupy bazy danych">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-sm text-[#64748B]">
          Codziennie o 03:00 UTC tworzony jest automatyczny backup (retencja 30 dni).
          Możesz też wymusić backup teraz.
        </p>
        <Button
          onClick={create}
          disabled={creating}
          className="brut brut-hover bg-[#FEF08A] text-[#0F172A] font-bold rounded-md"
          data-testid="create-backup-btn"
        >
          <Download className="w-4 h-4 mr-2" strokeWidth={2.5} />
          {creating ? "Tworzę..." : "Utwórz backup teraz"}
        </Button>
      </div>
      <div className="space-y-2">
        {list.length === 0 && <div className="text-sm text-[#64748B]">Brak backupów.</div>}
        {list.map((b) => (
          <div key={b.id} className="brut border-2 border-[#0F172A] rounded-md p-3 bg-white flex items-center justify-between flex-wrap gap-2" data-testid={`backup-row-${b.id}`}>
            <div className="text-sm">
              <div className="font-bold">{b.key}</div>
              <div className="text-xs text-[#64748B]">
                {new Date(b.created_at).toLocaleString("pl")} · {(b.size_bytes / 1024).toFixed(1)} KB · {b.trigger}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => openPreview(b)}
                className="brut brut-hover bg-white text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md"
                data-testid={`preview-backup-btn-${b.id}`}
              >
                <Eye className="w-4 h-4 mr-1" strokeWidth={2.5} />
                Podgląd
              </Button>
              <Button
                size="sm"
                onClick={() => askRestore(b.key)}
                className="brut brut-hover bg-white text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="restore-backup-btn"
              >
                Przywróć
              </Button>
              <Button
                size="sm"
                onClick={() => remove(b)}
                className="brut brut-hover bg-[#FECACA] text-[#0F172A] border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="delete-backup-btn"
              >
                <Trash2 className="w-4 h-4" strokeWidth={2.5} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <AlertDialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <AlertDialogContent className="brut border-2 border-[#0F172A] rounded-md bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-[#EF4444]" />
              Potwierdź przywrócenie
            </AlertDialogTitle>
            <AlertDialogDescription>
              Operacja ZASTĄPI wszystkie istniejące dane (pytania, klasy, sesje, nauczyciele)
              zawartością backupu <b>{restoreKey}</b>. Wpisz słowo <b>RESTORE</b> aby potwierdzić.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={restoreConfirm}
            onChange={(e) => setRestoreConfirm(e.target.value)}
            placeholder="RESTORE"
            className="brut-input border-2 border-[#0F172A] rounded-md"
            data-testid="restore-confirm-input"
          />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-cancel-btn">Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={doRestore}
              disabled={busy || restoreConfirm !== "RESTORE"}
              data-testid="restore-confirm-btn"
            >
              {busy ? "Przywracam..." : "Przywróć teraz"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview dialog */}
      <AlertDialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <AlertDialogContent className="brut border-2 border-[#0F172A] rounded-md bg-white max-w-3xl" data-testid="backup-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Eye />
              Podgląd backupu
            </AlertDialogTitle>
            <AlertDialogDescription>
              Read-only podgląd zawartości — bez przywracania. Sprawdź, czy backup
              zawiera dane, których oczekujesz, zanim klikniesz &bdquo;Przywróć&rdquo;.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {previewLoading && (
            <div className="text-sm text-[#64748B] italic py-4">Ładowanie podglądu…</div>
          )}
          {previewData && (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="text-xs font-mono text-[#64748B] break-all">
                <b>Plik:</b> {previewData.backup_key}<br />
                <b>Utworzony:</b> {previewData.backup_created_at &&
                  new Date(previewData.backup_created_at).toLocaleString("pl")}
              </div>
              {Object.entries(previewData.collections || {}).map(([name, info]) => (
                <div
                  key={name}
                  className="brut border-2 border-[#0F172A] rounded-md p-2 bg-[#F8FAFC]"
                  data-testid={`preview-collection-${name}`}
                >
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                    <span className="font-black">{name}</span>
                    <span className="brut border-2 border-[#0F172A] rounded-md px-2 py-0.5 text-[10px] font-black bg-[#FEF08A]">
                      {info.count} {info.count === 1 ? "wiersz" : "wierszy"}
                    </span>
                  </div>
                  <details>
                    <summary className="text-xs cursor-pointer text-[#475569]">
                      Pokaż próbkę {info.sample?.length || 0} dokumentów
                    </summary>
                    <pre className="text-[10px] font-mono bg-white border border-[#E2E8F0] rounded p-2 mt-1 overflow-x-auto whitespace-pre-wrap break-all">
{JSON.stringify(info.sample, null, 2)}
                    </pre>
                  </details>
                </div>
              ))}
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="preview-close-btn">Zamknij</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Section>
  );
}


export default BackupsTab;
